import React, { Fragment, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { cn } from "@ndd/ui";
import type { WorkspaceThreadSummary } from "../../../../shared/api-types";
import type { RailRd } from "../../lib/rail-sections";
import { chatThreadLabel } from "../../lib/rd-page-tabs";
import type { RenderRailLink } from "./always-rail-parts";

// The chats nested under an RD row in the single rail (NO-754): the viewer's
// own chats about that RD, newest first, "Show N more", and New chat. A leaf
// module (`import React`, relative imports, no router, no queries) so
// `tsx --test` renders it; the composer (always-rail-chats.tsx) decides which
// chats show and supplies real links through `renderLink`.

export type RailRdChatsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; chats: WorkspaceThreadSummary[]; hiddenCount: number };

function chatRowClass(current: boolean): string {
  return cn(
    "flex h-8 items-center rounded-lg px-2.5 font-sans text-body transition-colors brand:rounded-md brand:font-body",
    current
      ? "bg-accent font-semibold text-foreground brand:bg-card brand:shadow-[inset_0_0_0_1.5px_var(--primary)]"
      : "font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
  );
}

const QUIET_LINE = "px-2.5 py-1.5 text-body-sm text-subtle";

function RailChatRows({
  rd,
  chats,
  currentChatId,
  renderLink,
}: {
  rd: RailRd;
  chats: WorkspaceThreadSummary[];
  currentChatId: string | null;
  renderLink: RenderRailLink;
}): ReactElement {
  return (
    <>
      {chats.map((chat) => (
        <Fragment key={chat.chatId}>
          {renderLink({
            href: {
              to: "/rds/$rdId/chats/$chatId",
              rdId: rd.slug,
              chatId: chat.chatId,
            },
            current: chat.chatId === currentChatId,
            className: chatRowClass(chat.chatId === currentChatId),
            children: <span className="truncate">{chatThreadLabel(chat)}</span>,
          })}
        </Fragment>
      ))}
    </>
  );
}

function RailChatsBody({
  rd,
  state,
  currentChatId,
  onShowMore,
  renderLink,
}: {
  rd: RailRd;
  state: RailRdChatsState;
  currentChatId: string | null;
  onShowMore: () => void;
  renderLink: RenderRailLink;
}): ReactElement {
  if (state.status === "loading") return <p className={QUIET_LINE}>Loading chats…</p>;

  if (state.status === "error") return <p className={QUIET_LINE}>Chats unavailable</p>;

  return (
    <>
      <RailChatRows
        rd={rd}
        chats={state.chats}
        currentChatId={currentChatId}
        renderLink={renderLink}
      />
      {state.hiddenCount > 0 ? (
        <button
          type="button"
          onClick={onShowMore}
          className="flex h-8 items-center rounded-lg px-2.5 text-left font-sans text-body-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          Show {state.hiddenCount} more
        </button>
      ) : null}
    </>
  );
}

/**
 * One RD's chats, indented under its row so the titles line up with the RD's
 * name. Hidden in the icon strip; the drawer below lg always shows them.
 */
export function RailRdChats({
  rd,
  state,
  currentChatId,
  newChatCurrent,
  collapsed,
  onShowMore,
  renderLink,
}: {
  rd: RailRd;
  state: RailRdChatsState;
  /** The open thread's id, only when it is about this RD. */
  currentChatId: string | null;
  newChatCurrent: boolean;
  collapsed: boolean;
  onShowMore: () => void;
  renderLink: RenderRailLink;
}): ReactElement {
  return (
    <div
      role="group"
      aria-label={`Chats about ${rd.name}`}
      className={cn("flex flex-col gap-px pb-1 pl-10", collapsed && "lg:hidden")}
    >
      <RailChatsBody
        rd={rd}
        state={state}
        currentChatId={currentChatId}
        onShowMore={onShowMore}
        renderLink={renderLink}
      />
      {renderLink({
        href: { to: "/rds/$rdId/chats/new", rdId: rd.slug },
        current: newChatCurrent,
        className: cn(chatRowClass(newChatCurrent), "gap-1.5"),
        children: (
          <>
            <Plus aria-hidden="true" className="size-[14px]" />
            New chat
          </>
        ),
      })}
    </div>
  );
}
